import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SentEInvoicesTable } from "@/components/SentEInvoicesTable";
import { NetworkReferenceSearch } from "@/components/NetworkReferenceSearch";
import type { components } from "@dolfin/api-admin";

type DeliveryState = NonNullable<components["schemas"]["OutboundEInvoiceState"]>;

const stateOptions: { value: DeliveryState | "any"; label: string }[] = [
  { value: "any", label: "All states" },
  { value: "DeliveryFailed", label: "Failed - refused or undeliverable" },
  { value: "Abandoned", label: "Abandoned - never left, or refused before sending" },
  { value: "Validated", label: "Validated - not yet handed over" },
  { value: "Submitted", label: "Submitted - awaiting the send's result" },
  { value: "Accepted", label: "Accepted - awaiting the network's verdict (SDI)" },
  { value: "Delivered", label: "Delivered" },
  { value: "MadeAvailable", label: "Made available - issued, not delivered" },
];

/** Sent e-invoices across all organisations; support is often handed a network id without an organisation. */
export function SentEInvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);

  const stateParam = searchParams.get("state");
  const state: DeliveryState | "any" = stateOptions.some((o) => o.value === stateParam)
    ? (stateParam as DeliveryState | "any")
    : "any";
  const reference = searchParams.get("ref") ?? undefined;
  const organisationId = searchParams.get("organisationId") ?? undefined;

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) params.set(key, value);
        else params.delete(key);
        return params;
      },
      { replace: true },
    );
  }

  // In-flight SDI deliveries settle on their own (the verdict arrives later), so the list refreshes.
  useEffect(() => {
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Sent E-Invoices</h1>
      <p className="mb-6 max-w-3xl text-sm text-muted-foreground">
        Every invoice and credit note sent over an e-invoicing network, across organisations. Search by
        the id the network gave a document (for SDI, the IdentificativoSdI) - that also finds an earlier
        attempt that was rejected and corrected. Newest first, paged.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <NetworkReferenceSearch value={reference} onChange={(v) => updateParam("ref", v)} />
        <Select value={state} onValueChange={(v) => updateParam("state", v === "any" ? null : v)}>
          <SelectTrigger className="w-80">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {organisationId && (
          <Button variant="ghost" size="sm" onClick={() => updateParam("organisationId", null)}>
            One organisation only - show all
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <SentEInvoicesTable
        state={state === "any" ? undefined : state}
        networkReference={reference}
        organisationId={organisationId}
        refreshKey={refreshKey}
      />
    </div>
  );
}
